import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

import CallServer from '../../api/CallServerV2';
import { selectTranslations } from "../../services/i18n/i18nSlice";
import BRAND, { useLogo } from "../../brand";
import {Text} from "../../components/Text";
import {TitleH1} from "../../components/TitleH1";
import {Button} from "../../components/Button";
import Anchor from "../../components/Anchor";
import useSamlLogin from "./useSamlLogin";

import './LoginScreen.css'

// fields the user can optionally add, with the backend limits
const FIELDS = [
    { id: 'codice_fiscale_or_document', maxLength: 64, required: true },
    { id: 'course', maxLength: 200, required: true },
    { id: 'application_email', maxLength: 254, minLength: 3, required: true, type: 'email' },
    { id: 'notes', maxLength: 2000, as: 'textarea' },
]

// first SAML login the backend couldn't match to a graduatoria: it redirects here with a 30 min token,
// no session. The request already has what the office needs, the form only adds optional info.
export default function ManualAccessRequest () {
    const logo = useLogo()
    const t = useSelector(selectTranslations).manual_access
    const [startSamlLogin, samlLoading] = useSamlLogin()
    const token = useSearchParams()[0].get('token')

    // loading | draft | submitted | resolved | rejected | expired (401) | closed (400) | error
    const [status, setStatus] = useState('loading')
    const [values, setValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [showForm, setShowForm] = useState(false)
    const [saving, setSaving] = useState(false)

    const applyResponse = (res) => {
        if (res.success) {
            setStatus(res.data.state)
            setValues(Object.fromEntries(FIELDS.map(f => [f.id, res.data[f.id] || ''])))
            return true
        }
        if (res.status === 422 && Array.isArray(res.error)) {
            // FastAPI validation: loc = ['body', field]
            setFieldErrors(Object.fromEntries(res.error.map(e => [e.loc?.[e.loc.length - 1], e.msg])))
            return false
        }
        setStatus(res.status === 401 ? 'expired' : res.status === 400 ? 'closed' : 'error')
        return false
    }

    useEffect(()=>{
        if (!token) return setStatus('expired')
        setStatus('loading')
        setShowForm(false)
        CallServer({ endpoint: '/manual_access_request', params: { token }, isGet: true, needAuthenticate: false }).then(applyResponse)
        // eslint-disable-next-line
    },[token])

    const submit = (e) => {
        e.preventDefault()
        setSaving(true)
        setFieldErrors({})
        CallServer({ endpoint: '/manual_access_request', body: { token, ...values }, needAuthenticate: false })
            .then(res => { if (applyResponse(res)) setShowForm(false) })
            .finally(() => setSaving(false))
    }

    const loginButton = (
        <Button className="w-100 mx-auto position-relative" onClick={startSamlLogin}>
            <div className={samlLoading ? 'invisible' : ''}>{t.login_again}</div>
            {samlLoading && <Spinner size="sm" className="position-absolute top-50 start-50 translate-middle" />}
        </Button>
    )

    const pending = status === 'draft' || status === 'submitted'
    const message = {
        draft: [t.pending_title, t.pending_text],
        submitted: [t.pending_title, t.pending_text],
        resolved: [t.resolved_title, t.resolved_text],
        rejected: [t.rejected_title, t.rejected_text],
        expired: [t.expired_title, t.expired_text],
        closed: [t.closed_title, t.closed_text],
        error: [t.error_title, t.error_text],
    }[status]

    return(
        <div id="loginScreen">
            <div className="login_main">
                <div className="fh-div d-flex flex-column align-items-center justify-content-between">
                    <div className="top w-100">
                        <img src={logo} className="login_logo" alt={BRAND.name} />
                        {status === 'loading' ?
                            <div className="d-flex justify-content-center mt-5"><Spinner /></div>
                        : <>
                            <TitleH1 className="login_title">{message[0]}</TitleH1>
                            <Text className="login_text mb-4">{message[1]}</Text>
                            {status === 'submitted' && !showForm && <Alert variant="success">{t.submitted}</Alert>}
                            {pending && !showForm &&
                                <Anchor className="d-block text-center" fontSize="14px" href="#" onClick={(e)=>{e.preventDefault(); setShowForm(true)}}>
                                    {status === 'submitted' ? t.edit_info : t.add_info}
                                </Anchor>
                            }
                            {pending && showForm &&
                                <Form className="pb-4" onSubmit={submit}>
                                    <Text className="login_text mb-3">{t.form_intro}</Text>
                                    {FIELDS.map(f =>
                                        <Form.Group key={f.id} className="mb-3" controlId={`mar_${f.id}`}>
                                            <Form.Label>{t.fields[f.id]}{f.required && '*'}</Form.Label>
                                            <Form.Control as={f.as} rows={f.as ? 4 : undefined} type={f.type || 'text'}
                                                required={f.required} minLength={f.minLength} maxLength={f.maxLength}
                                                value={values[f.id] || ''} isInvalid={!!fieldErrors[f.id]}
                                                onChange={(e)=>setValues({...values, [f.id]: e.target.value})} />
                                            <Form.Control.Feedback type="invalid">{fieldErrors[f.id]}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                    <Button type="submit" className="w-100 mx-auto" disabled={saving}>{saving ? <Spinner size="sm" /> : t.send}</Button>
                                    <Anchor className="d-block text-center mt-3" fontSize="14px" href="#" onClick={(e)=>{e.preventDefault(); setShowForm(false)}}>{t.cancel}</Anchor>
                                </Form>
                            }
                        </>}
                    </div>
                    <div className="bottom pb-4 w-100">
                        {['resolved', 'expired', 'closed', 'error'].includes(status) && loginButton}
                    </div>
                </div>
            </div>
        </div>
    )
}
